import type { AxiosInstance } from 'axios';
import { BILLING_ENDPOINTS as E } from '../endpoints';
import type {
  ConfirmEducationRegistrationRequest,
  CreateEducationRegistrationRequest,
  CreateEducationRegistrationResult,
  EducationCheckout,
  EducationCourse,
  EducationMemberOptions,
  EducationRegistration,
  EducationSession,
  EducationSessionQuery,
  PaginatedEducationRegistrations,
  PaginatedEducationSessions,
} from '../types/education.type';

/**
 * 교육 호출부. 카탈로그(courses/sessions)는 비로그인 호출이 가능하고,
 * 접수·확정·내역은 로그인 필수다 — 본인 판정은 서버가 x-user-id 로 한다.
 *
 * @example
 * export const educationApi = createEducationApi(apiClient);
 */
export function createEducationApi(apiClient: AxiosInstance) {
  return {
    courses: (family?: string) =>
      apiClient
        .get<EducationCourse[]>(E.educationCourses, { params: { family } })
        .then((r) => r.data),

    course: (id: string) =>
      apiClient.get<EducationCourse>(E.educationCourse(id)).then((r) => r.data),

    sessions: (query: EducationSessionQuery = {}) =>
      apiClient
        .get<PaginatedEducationSessions>(E.educationSessions, { params: query })
        .then((r) => r.data),

    session: (id: string) =>
      apiClient
        .get<EducationSession>(E.educationSession(id))
        .then((r) => r.data),

    memberOptions: (sessionIds: string[] = []) =>
      apiClient
        .get<EducationMemberOptions>(E.educationMemberOptions, {
          params: { sessionIds },
          paramsSerializer: { indexes: null },
        })
        .then((r) => r.data),

    /**
     * 접수 생성. 유료면 응답의 `payment.orderId` 로 Toss 결제창을 연다 —
     * 이 시점에 정원이 홀드되고, 결제를 끝내지 않으면 서버가 회수한다.
     */
    createRegistration: (body: CreateEducationRegistrationRequest) =>
      apiClient
        .post<CreateEducationRegistrationResult>(
          E.educationRegistrations,
          body,
        )
        .then((r) => r.data),

    /** Toss 리다이렉트 후 결제 승인 + 접수 확정. 재호출해도 안전하다. */
    confirmRegistration: (
      id: string,
      body: ConfirmEducationRegistrationRequest,
    ) =>
      apiClient
        .post<EducationRegistration>(
          E.confirmEducationRegistration(id),
          body,
        )
        .then((r) => r.data),

    /**
     * 결제 재개. 응답의 orderId 로 결제창을 다시 연다.
     * 이미 승인된 결제였다면 서버가 접수를 확정하고 409 로 답한다 — 상세를 다시 읽으면 된다.
     */
    resumeRegistration: (id: string) =>
      apiClient
        .post<EducationCheckout>(E.resumeEducationRegistration(id))
        .then((r) => r.data),

    myRegistrations: (page = 1, limit = 10) =>
      apiClient
        .get<PaginatedEducationRegistrations>(E.myEducationRegistrations, {
          params: { page, limit },
        })
        .then((r) => r.data),

    myRegistration: (id: string) =>
      apiClient
        .get<EducationRegistration>(E.myEducationRegistration(id))
        .then((r) => r.data),
  };
}

export type EducationApi = ReturnType<typeof createEducationApi>;
